import { useState, useEffect, useMemo, useRef } from "react";
import { Tooltip, Typography, Box, Paper, Alert } from "@mui/material";
import {
  Accordion,
  AircraftTitleHeader,
  RevisionHeader,
  RevisionItem,
  ConfigurationItem,
  AccordionDetails,
} from "../AircraftAccordion/AircraftAccordionItems";
import { IAircraft, IAircraftType, IFullAircraftConfiguration } from "@vmosolutions/oc-interfaces";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import moment from "moment-timezone";
import EditIcon from "@mui/icons-material/Edit";
import AddCardIcon from "@mui/icons-material/AddCard";
import LibraryAddIcon from "@mui/icons-material/LibraryAdd";
import { getLabel } from "../utils/index";
import LoadingWrapper from "src/components/LoadingWrapper/LoadingWrapper";
import { config } from "process";

interface IAircraftInfoProps {
  activeAircraft: IAircraft;
  configs: any;
  setConfigs: any;
  setCurrentConfig: any;
  setCurrentRevision: any;
  setForm: any;
  currentConfig: IFullAircraftConfiguration;
  stagedAircraft: IAircraft;
  stagedConfig: IFullAircraftConfiguration[];
  setActiveAircraft: any;
}
export function AircraftInfo(props: IAircraftInfoProps): JSX.Element {
  const {
    activeAircraft,
    configs,
    setConfigs,
    setCurrentRevision,
    setCurrentConfig,
    setForm,
    currentConfig,
    stagedAircraft,
    stagedConfig,
    setActiveAircraft,
  } = props;
  const [expanded, setExpanded] = useState<string | false>("panel1");
  const [isLoadingConfigs, setIsLoadingConfigs] = useState<boolean>(false);

  const handleConfigurationAccordionChange = (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
    setExpanded(newExpanded ? panel : false);
  };

  useEffect(() => {
    if (configs && configs.length > 0) {
      const cfgidx = configs.findIndex((cfg: IFullAircraftConfiguration) => cfg._id === currentConfig?._id);
      if (cfgidx >= 0) {
        setExpanded(`panel-${cfgidx}`);
      }
    }
  }, [configs, currentConfig]);

  useEffect(() => {
    if (activeAircraft?._id && !stagedAircraft) {
      setIsLoadingConfigs(true);
      setConfigs([]);
      const getConfigs = async (): Promise<void> => {
        const response = await AmplifyAPIWrapper.Get<IFullAircraftConfiguration[]>(
          `/aircraftConfiguration?aircraftId=${activeAircraft._id}`,
          undefined
        );
        if (Array.isArray(response)) {
          setConfigs(response);
          setIsLoadingConfigs(false);
        }
      };
      getConfigs();
    }
  }, [activeAircraft, setConfigs, stagedAircraft]);

  moment.tz.setDefault("GMT");
  return (
    <Box sx={{ pl: 1, overflowY: "scroll", maxHeight: "calc(100svh - 310px)" }}>
      {activeAircraft && activeAircraft.hasOwnProperty("registration") && (
        <>
          <AircraftTitleHeader
            title={getLabel(activeAircraft)}
            icon={
              <Box sx={{ display: "flex", justifyContent: "end" }}>
                <Tooltip title="Edit Aircraft">
                  <EditIcon
                    fontSize="small"
                    sx={{ cursor: "pointer", marginRight: "6px" }}
                    onClick={(event) => {
                      event.stopPropagation();
                      setActiveAircraft(activeAircraft);
                      setCurrentConfig(undefined);
                      setCurrentRevision(undefined);
                      setForm("aircraft");
                    }}
                  />
                </Tooltip>
                <Tooltip title="Add New Configuration">
                  <AddCardIcon
                    fontSize="small"
                    sx={{ cursor: "pointer", marginRight: "6px" }}
                    onClick={() => {
                      setCurrentConfig(undefined);
                      setCurrentRevision(undefined);
                      setForm("config");
                    }}
                  />
                </Tooltip>
              </Box>
            }
          />

          {!stagedAircraft && isLoadingConfigs && (
            <Box sx={{ py: 3 }}>
              <LoadingWrapper animate={true} message="Loading Aircraft Configurations" />
            </Box>
          )}

          {configs &&
            configs.map((config: IFullAircraftConfiguration, cfgKey: number) => {
              return (
                <Paper
                  key={cfgKey}
                  elevation={4}
                  sx={{
                    transition: "background-color 350ms",
                    cursor: "pointer",
                  }}
                >
                  <Accordion
                    expanded={expanded === `panel-${cfgKey}`}
                    onChange={handleConfigurationAccordionChange(`panel-${cfgKey}`)}
                  >
                    <ConfigurationItem
                      aria-controls={`panel-${cfgKey}-config-content`}
                      id={`panel-${cfgKey}-config-header`}
                      onClick={(event) => {
                        event.stopPropagation();
                        setCurrentConfig(config);
                        setCurrentRevision(undefined);
                        setForm("config");
                      }}
                    >
                      <Typography sx={{ fontWeight: 900 }}>{config.title}</Typography>
                      <Tooltip title="Edit Configuration Details">
                        <EditIcon
                          fontSize="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            setCurrentConfig(config);
                            setCurrentRevision(undefined);
                            setForm("config");
                          }}
                        />
                      </Tooltip>
                    </ConfigurationItem>
                    <RevisionHeader
                      aria-controls={`panel-${cfgKey}-revision-content`}
                      id={`panel-${cfgKey}-revision-header`}
                    >
                      <Typography>Revisions</Typography>
                      <Tooltip title="Add New Revision">
                        <AddCardIcon
                          fontSize="small"
                          sx={{ cursor: "pointer" }}
                          onClick={(event) => {
                            event.stopPropagation();
                            setCurrentConfig(config);
                            setCurrentRevision(undefined);
                            setForm("revision");
                          }}
                        />
                      </Tooltip>
                    </RevisionHeader>
                    {config.revisions.length > 0 && (
                      <>
                        <AccordionDetails>
                          {config.revisions
                            .sort((a, b) => {
                              return a.effectiveDate.valueOf() - b.effectiveDate.valueOf();
                            })
                            .map((revision: any, revKey: number) => {
                              return (
                                <RevisionItem
                                  key={revKey}
                                  sx={{ cursor: "pointer" }}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setCurrentConfig(config);
                                    setCurrentRevision(revision);
                                    setForm("revision");
                                  }}
                                >
                                  {moment(revision.effectiveDate).format("MMM DD, YYYY (HH:mmA)")}
                                  <Tooltip title="Create From This (Use this revision to create a new one)">
                                    <LibraryAddIcon
                                      sx={{ cursor: "pointer" }}
                                      fontSize="small"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        setCurrentConfig(config);
                                        setCurrentRevision(revision);
                                        setForm("revision");
                                      }}
                                    />
                                  </Tooltip>
                                </RevisionItem>
                              );
                            })}
                        </AccordionDetails>
                      </>
                    )}
                  </Accordion>
                </Paper>
              );
            })}
          {stagedAircraft && (
            <Alert severity="warning" variant="filled" sx={{ mt: 1 }}>
              Your current Aircraft has not yet been saved, as it does not have a default configuration. After adding
              the first revision, this will be applied as the default, and the aircraft will be saved.
            </Alert>
          )}
        </>
      )}
    </Box>
  );
}
